/**
 * Authenticated fetch wrapper with automatic 401 handling.
 * 
 * This utility:
 * - Checks token validity before making requests
 * - Automatically attaches Authorization header
 * - Triggers logout on 401 responses or expired tokens
 * - Provides single-flight guard to prevent multiple simultaneous logouts
 */

import { getAuthToken, isTokenExpired, aggressiveLogout } from './authHelpers';

/**
 * Make an authenticated fetch request.
 * 
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options (will be merged with auth headers)
 * @param {Object} config - Additional configuration
 * @param {Function} config.disconnectChat - Optional callback to disconnect Stream Chat on logout
 * @param {boolean} config.requireAuth - If true, will logout if no token (default: true)
 * @param {boolean} config.skipAuthCheck - If true, skips pre-flight token check (default: false)
 * @returns {Promise<Response>} - The fetch response
 */
export const authFetch = async (url, options = {}, config = {}) => {
  const { disconnectChat, requireAuth = true, skipAuthCheck = false } = config;

  // Get the current token
  const token = getAuthToken();

  // Check if we need auth but don't have a token
  if (requireAuth && !token) {
    console.warn('🔐 authFetch: No token available, triggering logout');
    aggressiveLogout(disconnectChat, { reason: 'session_expired' });
    throw new Error('No authentication token available');
  }

  // Check if token is expired (unless skipping pre-flight check)
  if (token && !skipAuthCheck && isTokenExpired(token)) {
    console.warn('🔐 authFetch: Token expired before request, triggering logout');
    aggressiveLogout(disconnectChat, { reason: 'session_expired' });
    throw new Error('Authentication token expired');
  }

  // Merge headers with Authorization
  const headers = {
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make the request
  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized
  if (response.status === 401) {
    console.warn('🔐 authFetch: Received 401, triggering logout');
    aggressiveLogout(disconnectChat, { reason: 'session_expired' });
    throw new Error('Unauthorized - session expired');
  }

  return response;
};

/**
 * Make an authenticated fetch request and parse JSON response.
 * 
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options (will be merged with auth headers)
 * @param {Object} config - Additional configuration (same as authFetch)
 * @returns {Promise<{response: Response, data: any}>} - The response and parsed JSON data
 */
export const authFetchJson = async (url, options = {}, config = {}) => {
  // Ensure Content-Type is set for JSON requests if body is provided
  const headers = {
    ...options.headers,
  };
  
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await authFetch(url, { ...options, headers }, config);
  
  let data;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }

  return { response, data };
};

/**
 * Create a pre-configured authFetch with disconnectChat callback.
 * Useful for components that need to make multiple authenticated requests.
 * 
 * @param {Function} disconnectChat - Callback to disconnect Stream Chat
 * @returns {Object} - Object with authFetch and authFetchJson methods
 */
export const createAuthFetch = (disconnectChat) => {
  return {
    fetch: (url, options = {}, config = {}) => 
      authFetch(url, options, { ...config, disconnectChat }),
    fetchJson: (url, options = {}, config = {}) => 
      authFetchJson(url, options, { ...config, disconnectChat }),
  };
};

export default authFetch;
